import { useQuery } from '@apollo/client';
import { clsx } from 'clsx';
import {
    type ReactNode,
    useId,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import {
    Icon,
    MultiSelect,
    OverflowTooltipLabel,
    Pill,
    Tooltip,
} from '@ics-portal/guardian';
import { GET_PROJECT_TAGS_ME } from '@api/queries/projects';
import { sortAlphanumeric } from '@utils/sorting';
import { useToast } from '@contexts/GlobalUiContext';
import s from './ProjectTags.module.scss';

export interface ProjectTagOption {
    id: string;
    name: string;
}

interface ProjectTagMultiSelectChangeEvent {
    value: unknown;
}

interface ProjectTagChipProps {
    tag: ProjectTagOption;
    variant?: 'card' | 'infoCard';
}

export function ProjectTagChip({
    tag,
    variant = 'card',
}: ProjectTagChipProps): ReactNode {
    return (
        <Pill
            color={variant === 'card' ? 'n0' : undefined}
            className={clsx(
                'inline-flex align-items-center min-w-0 max-w-full white-space-nowrap',
                s.tagChip,
                variant === 'card' && s.tagChipCard,
            )}
        >
            <OverflowTooltipLabel
                label={tag.name}
                className="block min-w-0 max-w-full overflow-hidden text-overflow-ellipsis white-space-nowrap"
            />
        </Pill>
    );
}

interface ProjectTagsListProps {
    tags?: readonly (ProjectTagOption | null | undefined)[] | null;
    variant: 'card' | 'infoCard';
    emptyContent?: ReactNode;
}

function sortTags(
    tags?: readonly (ProjectTagOption | null | undefined)[] | null,
): ProjectTagOption[] {
    return [...(tags ?? [])]
        .filter((tag): tag is ProjectTagOption => Boolean(tag?.id && tag.name))
        .sort((first, second) => first.name.localeCompare(second.name));
}

export function ProjectTagsList({
    tags,
    variant,
    emptyContent = null,
}: ProjectTagsListProps): ReactNode {
    const sortedTags = sortTags(tags);
    const [expanded, setExpanded] = useState(false);
    const [hasOverflow, setHasOverflow] = useState(false);
    const tagsListRef = useRef<HTMLDivElement>(null);
    const toggleRef = useRef<HTMLButtonElement>(null);
    const tooltipId = useId();
    const tagIds = sortedTags.map((tag) => tag.id).join('|');

    useLayoutEffect(() => {
        const element = tagsListRef.current;
        if (!element || variant !== 'card' || !sortedTags.length) {
            setHasOverflow(false);
            setExpanded(false);
            return undefined;
        }

        const updateOverflow = (): void => {
            const firstTag = element.firstElementChild as HTMLElement | null;
            const rowHeight =
                firstTag?.getBoundingClientRect().height ??
                element.getBoundingClientRect().height;

            setHasOverflow(element.scrollHeight > rowHeight + 2);
        };

        updateOverflow();

        const resizeObserver = new ResizeObserver(updateOverflow);
        resizeObserver.observe(element);

        return () => {
            resizeObserver.disconnect();
        };
    }, [tagIds, sortedTags.length, variant]);

    if (!sortedTags.length && !emptyContent) {
        return null;
    }

    const content = sortedTags.length
        ? sortedTags.map((tag) => (
              <ProjectTagChip key={tag.id} tag={tag} variant={variant} />
          ))
        : emptyContent;

    if (variant === 'card') {
        return (
            <div className="flex flex-column w-full min-w-0">
                <div className="flex align-items-start gap-1 min-w-0">
                    <div
                        id={tooltipId}
                        ref={tagsListRef}
                        className={clsx(
                            'flex flex-wrap align-items-center min-w-0 gap-2',
                            !expanded && s.tagsListCardCollapsed,
                            !sortedTags.length && s.tagsListEmpty,
                        )}
                    >
                        {content}
                    </div>
                    {hasOverflow ? (
                        <>
                            <Tooltip
                                target={toggleRef}
                                content={
                                    expanded
                                        ? 'Show fewer tags'
                                        : 'Show more tags'
                                }
                                position="top"
                            />
                            <button
                                ref={toggleRef}
                                type="button"
                                className={clsx(
                                    'inline-flex flex-none align-items-center justify-content-center',
                                    s.tagsListToggle,
                                )}
                                aria-expanded={expanded}
                                aria-controls={tooltipId}
                                aria-label={
                                    expanded
                                        ? 'Show fewer tags'
                                        : 'Show more tags'
                                }
                                onClick={(event) => {
                                    event.preventDefault();
                                    event.stopPropagation();
                                    setExpanded((current) => !current);
                                }}
                            >
                                <Icon
                                    name={
                                        expanded
                                            ? 'keyboard_arrow_up'
                                            : 'keyboard_arrow_down'
                                    }
                                    fontSize="small"
                                />
                            </button>
                        </>
                    ) : null}
                </div>
            </div>
        );
    }

    return (
        <div
            className={clsx(
                'flex flex-wrap align-items-center min-w-0 gap-1',
                !sortedTags.length && s.tagsListEmpty,
            )}
        >
            {content}
        </div>
    );
}

interface ProjectTagSelectorProps {
    name?: 'tags';
    placeholder?: string;
    disabled?: boolean;
}

const MAX_TAG_NAME_LENGTH = 24;

function normalizeTagName(value: string): string {
    return value.trim().toLowerCase();
}

function getSelectedTagNames(value: unknown): string[] {
    if (!Array.isArray(value)) {
        return [];
    }

    return value.filter(
        (name): name is string =>
            typeof name === 'string' && name.trim().length > 0,
    );
}

export function ProjectTagSelector({
    name = 'tags',
    placeholder = 'Search or add tag',
    disabled,
}: ProjectTagSelectorProps): ReactNode {
    const { control } = useFormContext();
    const { pushErrorToast } = useToast();

    const { data } = useQuery(GET_PROJECT_TAGS_ME, {
        fetchPolicy: 'cache-and-network',
    });

    const knownTagNames = useMemo(
        () => data?.projectTagsMe ?? [],
        [data?.projectTagsMe],
    );

    return (
        <Controller
            name={name}
            control={control}
            render={({ field }) => {
                const selectedNames = getSelectedTagNames(field.value);
                const selectedTags: ProjectTagOption[] = selectedNames.map(
                    (tagName) => ({ id: tagName, name: tagName }),
                );
                const selectedNormalizedNames = new Set(
                    selectedNames.map(normalizeTagName),
                );
                const availableTagNames = knownTagNames
                    .filter(
                        (tagName) =>
                            !selectedNormalizedNames.has(
                                normalizeTagName(tagName),
                            ),
                    )
                    .sort(sortAlphanumeric);
                const options: ProjectTagOption[] = [
                    ...selectedTags,
                    ...availableTagNames.map((tagName) => ({
                        id: tagName,
                        name: tagName,
                    })),
                ];
                const setSelectedNames = (names: string[]): void => {
                    field.onChange(names);
                };

                const addTagName = (
                    value: string,
                    baseSelectedNames = selectedNames,
                ): void => {
                    const trimmedName = value.trim();
                    if (!trimmedName) {
                        return;
                    }

                    if ([...trimmedName].length > MAX_TAG_NAME_LENGTH) {
                        pushErrorToast({
                            detail: `Tag name cannot be longer than ${MAX_TAG_NAME_LENGTH} characters`,
                        });
                        return;
                    }

                    const normalizedName = normalizeTagName(trimmedName);
                    if (
                        baseSelectedNames.some(
                            (selectedName) =>
                                normalizeTagName(selectedName) ===
                                normalizedName,
                        )
                    ) {
                        return;
                    }

                    setSelectedNames([...baseSelectedNames, trimmedName]);
                };

                const handleChange = (
                    event: ProjectTagMultiSelectChangeEvent,
                ): void => {
                    setSelectedNames(getSelectedTagNames(event.value));
                };

                return (
                    <div className="flex flex-column gap-2 w-full min-w-0">
                        <MultiSelect
                            value={selectedNames}
                            options={options}
                            optionLabel="name"
                            optionValue="id"
                            dataKey="id"
                            selectedValueLayout="external"
                            filterPlaceholder="Search or add tag"
                            placeholder={placeholder}
                            emptyMessage="No matching tags"
                            emptyFilterMessage="No matching tags"
                            className="w-full"
                            wrapperClassName="w-full"
                            truncateLabel
                            wrapItems
                            creatable
                            onCreateOption={(newTagName) => {
                                addTagName(newTagName);
                            }}
                            disabled={disabled}
                            appendTo={
                                typeof document === 'undefined'
                                    ? undefined
                                    : document.body
                            }
                            onBlur={field.onBlur}
                            onChange={handleChange}
                        />
                    </div>
                );
            }}
        />
    );
}
